import { z } from 'zod';

/**
 * All optional — an empty string clears a link, omitting the key leaves it
 * untouched isn't supported here (the form always sends the full object).
 */
export const socialLinksSchema = z.object({
  linkedin: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  twitter: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  github: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  youtube: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
});
export type SocialLinksInput = z.infer<typeof socialLinksSchema>;

export const updateSiteSettingsSchema = z.object({
  address: z.string().trim().min(1, 'Address is required').max(500),
  phone: z.string().trim().min(1, 'Phone is required').max(30),
  contactEmail: z.string().trim().email('Must be a valid email'),
  salesEmail: z.string().trim().email('Must be a valid email'),
  socialLinks: socialLinksSchema,
});
export type UpdateSiteSettingsInput = z.infer<typeof updateSiteSettingsSchema>;

/**
 * The public site's fixed set of static, non-slug-driven pages — each already
 * renders its own `<PageMeta>` with hardcoded defaults. Per-item detail pages
 * (blog posts, services, portfolio items) keep sourcing SEO from their own
 * data files; they aren't part of this override registry.
 */
export const KNOWN_PAGE_PATHS = [
  '/',
  '/about',
  '/services',
  '/portfolio',
  '/careers',
  '/blog',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
] as const;
export type KnownPagePath = (typeof KNOWN_PAGE_PATHS)[number];

export const upsertPageSeoSchema = z.object({
  path: z.enum(KNOWN_PAGE_PATHS),
  title: z.string().trim().min(1, 'Title is required').max(70),
  description: z.string().trim().min(1, 'Description is required').max(200),
  ogImage: z.string().trim().url('Must be a valid URL').optional().or(z.literal('')),
  noIndex: z.boolean().default(false),
});
export type UpsertPageSeoInput = z.infer<typeof upsertPageSeoSchema>;

/**
 * `path` values contain slashes (e.g. `/services`), which don't survive as an
 * Express route param cleanly — used as a query-string schema for the
 * DELETE-a-page-SEO-override endpoint instead of `/page-seo/:path`.
 */
export const pageSeoPathQuerySchema = z.object({
  path: z.enum(KNOWN_PAGE_PATHS),
});
export type PageSeoPathQuery = z.infer<typeof pageSeoPathQuerySchema>;
